import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadCountries, type Country } from '../utils/countries';

interface UseCountriesResult {
  countries: Country[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to load countries dynamically based on current language
 * Automatically updates when language changes
 */
export function useCountries(): UseCountriesResult {
  const { i18n } = useTranslation();
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadCountriesForLanguage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const currentLanguage = i18n.language || 'en';
        const loadedCountries = await loadCountries(currentLanguage);
        setCountries(loadedCountries);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load countries');
        setError(error);
        console.error('[useCountries] Error loading countries:', error);
        // Set empty array on error to prevent UI breakage
        setCountries([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCountriesForLanguage();
  }, [i18n.language]);

  return { countries, isLoading, error };
}

